import React, { useEffect, useState, useMemo } from "react";
import api from "../api";
import { useNavigate } from "react-router-dom";

/* ---------------- RAZORPAY LOADER ---------------- */
const loadRazorpay = () => {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);

    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
};

export default function MySubscription() {
  const nav = useNavigate();

  const [subResponse, setSubResponse] = useState(null);
  const [subjects, setSubjects] = useState([]);
  const [selected, setSelected] = useState([]);

  const [billing, setBilling] = useState("monthly");

  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);

  const [error, setError] = useState(null);

  /* ---------------- LOAD DATA ---------------- */

  const loadData = async () => {
    try {
      setLoading(true);

      const subRes = await api.get("/subscription/my");
      const subjRes = await api.get("/student/available-subjects");

      setSubResponse(subRes.data);

      setSubjects(
        Array.isArray(subjRes.data?.subjects)
          ? subjRes.data.subjects
          : []
      );

      setSelected([]);
    } catch (err) {
      console.error(err);
      setError("Failed to load subscription data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  /* ---------------- DERIVED DATA ---------------- */

  const hasValidSubscription = subResponse?.has_subscription === true;

  const subscription = subResponse?.subscription || null;

  const ownedSubjects = useMemo(
    () => (Array.isArray(subscription?.subjects) ? subscription.subjects : []),
    [subscription]
  );

  const ownedSubjectIds = useMemo(
    () => ownedSubjects.map((s) => Number(s.id)),
    [ownedSubjects]
  );

  const ownedSubjectNames = useMemo(
    () => ownedSubjects.map((s) => s.name).filter(Boolean),
    [ownedSubjects]
  );

  const addableSubjects = useMemo(() => {
    return subjects.filter(
      (s) => !ownedSubjectIds.includes(Number(s.id))
    );
  }, [subjects, ownedSubjectIds]);

  /* ---------------- TOGGLE ---------------- */

  const toggleSubject = (id) => {
    if (ownedSubjectIds.includes(id)) return;

    setSelected((prev) =>
      prev.includes(id)
        ? prev.filter((x) => x !== id)
        : [...prev, id]
    );
  };

  /* ---------------- PRICE CALCULATION ---------------- */

  const total = selected.length;

  const free = Math.floor(total / 5);

  const payable = total - free;

  const unit = billing === "monthly" ? 10 : 100;

  const amount = payable * unit;

  /* ---------------- ADD SUBJECT PAYMENT ---------------- */

  const addSubjectsWithPayment = async () => {
    if (selected.length === 0) {
      alert("Please select at least one subject");
      return;
    }

    setProcessing(true);

    const razorpayLoaded = await loadRazorpay();

    if (!razorpayLoaded) {
      alert("Razorpay SDK failed to load");
      setProcessing(false);
      return;
    }

    try {
      const res = await api.post("/payment/add-subjects/create-order", {
        subject_ids: selected,
        billing_type: billing,
      });

      openRazorpay(res.data);
    } catch (err) {
      console.error(err);
      alert("Failed to initiate payment");
      setProcessing(false);
    }
  };

  /* ---------------- OPEN RAZORPAY ---------------- */

  const openRazorpay = (order) => {
    const options = {
      key: process.env.REACT_APP_RAZORPAY_KEY,
      amount: order.amount,
      currency: "INR",
      order_id: order.order_id,

      name: "EduAt10",
      description: "Add Subjects to Subscription",

      handler: async function (response) {
        try {
          await api.post("/payment/add-subjects/verify", {
            razorpay_order_id: response.razorpay_order_id,
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_signature: response.razorpay_signature,
          });

          alert("✅ Subjects added successfully");

          setSelected([]);

          await loadData();
        } catch (err) {
          console.error(err);
          alert("Payment verification failed");
        } finally {
          setProcessing(false);
        }
      },

      modal: {
        ondismiss: () => setProcessing(false),
      },

      theme: {
        color: "#16a34a",
      },
    };

    new window.Razorpay(options).open();
  };

  /* ---------------- UI STATES ---------------- */

  if (loading) return <div style={styles.loading}>⏳ Loading...</div>;

  if (error) return <div style={styles.error}>{error}</div>;

  if (!hasValidSubscription) {
    return (
      <div style={styles.noSubContainer}>
        <h2>No Active Subscription ❌</h2>

        <button
          style={{ ...styles.buyBtn, background: "#2563eb" }}
          onClick={() => nav("/subscription")}
        >
          Buy Subscription
        </button>
      </div>
    );
  }

  /* ---------------- UI ---------------- */

  return (
    <div style={page}>
      <div style={overlay}>
        <div style={card}>
          <img src="/logo.png" alt="EduAt10" style={logo} />

          <h2>🎟️ My Subscription</h2>

          <button
            onClick={() => nav("/user/dashboard")}
            style={backBtn}
          >
            ← Back to Dashboard
          </button>

          <div style={styles.card}>
            <p><b>Status:</b> {subscription?.status?.toUpperCase()}</p>
            <p><b>Class:</b> {subscription?.class_id ?? "N/A"}</p>
            <p><b>Plan:</b> {subscription?.plan_type}</p>
            <p><b>Starts:</b> {new Date(subscription.starts_at).toLocaleDateString()}</p>
            <p><b>Expires:</b> {new Date(subscription.ends_at).toLocaleDateString()}</p>
            <p>
              <b>Subscribed Subjects:</b>{" "}
              {ownedSubjectNames.length ? ownedSubjectNames.join(", ") : "None"}
            </p>
          </div>

          <h3 style={{ marginTop: 25 }}>➕ Add More Subjects</h3>

          {addableSubjects.map((s) => {
            const id = Number(s.id);

            return (
              <label key={id} style={styles.subjectItem}>
                <input
                  type="checkbox"
                  checked={selected.includes(id)}
                  onChange={() => toggleSubject(id)}
                />
                <span style={{ marginLeft: 8 }}>{s.name}</span>
              </label>
            );
          })}

          {/* BILLING */}

          {selected.length > 0 && (
            <>
              <h3 style={{ marginTop: 20 }}>Billing</h3>

              <label style={styles.subjectItem}>
                <input
                  type="radio"
                  checked={billing === "monthly"}
                  onChange={() => setBilling("monthly")}
                />
                Monthly ₹10
              </label>

              <label style={styles.subjectItem}>
                <input
                  type="radio"
                  checked={billing === "yearly"}
                  onChange={() => setBilling("yearly")}
                />
                Yearly ₹100
              </label>

              {/* SUMMARY */}

              <div style={styles.summary}>
                <p>Total Subjects: {total}</p>
                <p>Free Subjects: {free}</p>
                <p>Payable Subjects: {payable}</p>
                <h3>Amount: ₹{amount}</h3>
              </div>

              <button
                disabled={processing}
                onClick={addSubjectsWithPayment}
                style={{
                  ...styles.buyBtn,
                  marginTop: 15,
                  background: processing ? "#999" : "#16a34a",
                }}
              >
                {processing ? "Processing..." : `Pay ₹${amount}`}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/* ---------------- STYLES ---------------- */

const page = {
  minHeight: "100vh",
  backgroundImage: "url('/eduat10-bg.png')",
  backgroundSize: "cover",
};

const overlay = {
  minHeight: "100vh",
  background: "rgba(0,0,0,0.3)",
  padding: 20,
};

const card = {
  maxWidth: 900,
  margin: "auto",
  background: "rgba(255,255,255,0.95)",
  borderRadius: 16,
  padding: 30,
};

const logo = { width: 120 };

const backBtn = {
  padding: "10px 16px",
  background: "#e2e8f0",
  border: "none",
  borderRadius: 8,
  cursor: "pointer",
  marginBottom: 10,
};

const styles = {
  loading: { padding: 20, fontSize: 18 },
  error: { padding: 20, color: "red", fontSize: 18 },
  noSubContainer: { padding: 20, textAlign: "center" },

  buyBtn: {
    padding: "10px 20px",
    color: "white",
    border: "none",
    borderRadius: 6,
    cursor: "pointer",
    fontSize: 16,
  },

  card: {
    padding: 15,
    border: "1px solid #ddd",
    borderRadius: 8,
    background: "#f9fafc",
    marginTop: 15,
  },

  subjectItem: {
    display: "block",
    padding: 10,
    border: "1px solid #ddd",
    borderRadius: 6,
    marginTop: 6,
  },

  summary: {
    marginTop: 12,
    padding: 10,
    background: "#f1f5f9",
    borderRadius: 6,
  },
};
